import { TextField } from '@mui/material';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { cleanUpPayload } from '../../../../shared/functions/Functions';
import { Form } from 'react-bootstrap';
import { IRegisterStepperProps } from '../../../../model/shared/IRegistration';
import { IDiscoveryRegistrationStepThree } from '../dto/IDiscoveryRegistration';
import { StepperButtons } from '../../brand/components/StepperButtons';
import { AuthService } from '../../../service';
import { ErrorToast, SuccessToast } from '../../../../utils/toasters';
import { gtmService } from '../../../../utils/gtm.service';

export const StepThree = ({ stepIdNumber, handleBackStep }: IRegisterStepperProps) => {
    const [currentUser, setCurrentUser] = useState<IDiscoveryRegistrationStepThree>({
        brandCompany: { contact: { country: '' }, displayName: '', industry: '', nbEmployees: '' },
        contact: { phone: '' },
        firstName: '',
        id: 0,
        lastName: '',
        locale: 'en',
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { country, CompanyIndustryEnum, CompanyEmployeesEnum } = useSelector((state: IAllStates) => state.enums);
    const savedData = useSelector((state: IAllStates) => state.registration);

    useEffect(() => {
        if (!!savedData?.id) {
            setCurrentUser(cleanUpPayload(savedData) as IDiscoveryRegistrationStepThree);
        }
    }, [savedData?.id]);

    const changeHandler = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, isCompanyName = false) => {
        const { name, value } = event.target;
        setCurrentUser(prev =>
            isCompanyName
                ? { ...prev, brandCompany: { ...prev.brandCompany, displayName: value } }
                : { ...prev, [name]: value }
        );
    };

    const changeAutoCompleteHandler = (paramKey: string, paramValue: string | number | string[], type?: string) => {
        if (type === 'country') {
            setCurrentUser((prev: any) => ({
                ...prev,
                brandCompany: {
                    ...prev.brandCompany,
                    contact: {
                        ...prev.brandCompany.contact,
                        [type]: paramValue,
                    },
                },
            }));
        } else {
            setCurrentUser((prev: any) => ({
                ...prev,
                brandCompany: {
                    ...prev.brandCompany,
                    [paramKey]: paramValue,
                },
            }));
        }
    };

    const changePhoneHandler = (value: string) => {
        setCurrentUser(prev => ({
            ...prev,
            contact: {
                ...prev?.contact,
                phone: value,
            },
        }));
    };

    const changeLocaleHandler = (paramKey: string, paramValue: string | string[] | number) => {
        setCurrentUser((prevState: IDiscoveryRegistrationStepThree) => ({ ...prevState, [paramKey]: paramValue }));
    };

    const handleNextStep = () => {
        AuthService.setPersonalInfoGlobalDiscovery(currentUser)
            .then(response => {
                gtmService('discovery_registration_successful');
                navigate('/');
                SuccessToast(t('auth.register.brand.step7.discovery_successfully_registration'));
            })
            .catch(error => ErrorToast(error));
    };

    return (
        <Form
            onSubmit={e => {
                e.preventDefault();
                handleNextStep();
            }}
        >
            <div className="col-12">
                <h3>{t('auth.register.influencer.step4.personal_info')}</h3>
                <p className="text-muted mb-4">{t('auth.register.influencer.step4.personal_desc')}</p>
            </div>

            <div className="row justify-content-center">
                <div className="col-md-6">
                    <TextField
                        label={t('auth.register.brand.step2.firstName')}
                        variant="outlined"
                        name="firstName"
                        onChange={changeHandler}
                        size="small"
                        required
                        value={currentUser?.firstName ?? ''}
                        className="w-100 my-2"
                    />
                </div>
                <div className="col-md-6">
                    <TextField
                        label={t('auth.register.brand.step2.lastName')}
                        variant="outlined"
                        name="lastName"
                        onChange={changeHandler}
                        size="small"
                        value={currentUser?.lastName ?? ''}
                        required
                        className="w-100 my-2"
                    />
                </div>
                <div className="col-md-6">
                    <PhoneInput
                        country="de"
                        value={currentUser?.contact?.phone ?? ''}
                        onChange={changePhoneHandler}
                        inputClass="w-100 my-2"
                    />
                </div>

                <div className="col-md-6">
                    <CustomAutocomplete
                        dataArray={['en', 'de']}
                        isWithImage={false}
                        inputLabel="locale"
                        classes="my-2"
                        defaultArray={currentUser?.locale}
                        isMultiple={false}
                        photoLabel=""
                        queryFilterKey="locale"
                        inputLabelTranslation={t('auth.register.brand.step7.language')}
                        handleChangeParams={changeLocaleHandler}
                    />
                </div>
                <div className="col-12 my-4">
                    <h3>{t('auth.register.brand.step4.companyInfo')}</h3>
                    <p className="text-muted mb-0">{t('auth.register.brand.step4.companyInfoDescription')}</p>
                </div>

                <div className="col-md-6">
                    <TextField
                        label={t('auth.register.brand.step4.companyName')}
                        variant="outlined"
                        name="displayName"
                        size="small"
                        value={currentUser?.brandCompany?.displayName ?? ''}
                        onChange={event => changeHandler(event, true)}
                        required
                        className="w-100 my-2"
                    />
                </div>
                <div className="col-md-6 my-2">
                    <CustomAutocomplete
                        dataArray={country ?? []}
                        defaultArray={currentUser?.brandCompany?.contact?.country as string}
                        isWithImage={true}
                        inputLabel="country"
                        isMultiple={false}
                        isRequired={true}
                        photoLabel="countryFlag"
                        queryFilterKey="country"
                        inputLabelTranslation={t('auth.register.brand.step2.country')}
                        handleChangeParams={(_, e) => changeAutoCompleteHandler(_, e, 'country')}
                    />
                </div>

                <div className="col-md-6 my-2">
                    <CustomAutocomplete
                        dataArray={CompanyIndustryEnum ?? []}
                        defaultArray={currentUser?.brandCompany?.industry as string}
                        isWithImage={false}
                        inputLabel="industry"
                        isMultiple={false}
                        photoLabel=""
                        isRequired={true}
                        queryFilterKey="industry"
                        inputLabelTranslation={t('auth.register.brand.step4.industry')}
                        handleChangeParams={changeAutoCompleteHandler}
                    />
                </div>
                <div className="col-md-6 my-2">
                    <CustomAutocomplete
                        dataArray={CompanyEmployeesEnum ?? []}
                        defaultArray={currentUser?.brandCompany.nbEmployees as string}
                        isWithImage={false}
                        inputLabel="nbEmployees"
                        isMultiple={false}
                        photoLabel=""
                        isRequired={true}
                        queryFilterKey="nbEmployees"
                        inputLabelTranslation={t('auth.register.brand.step4.employees')}
                        handleChangeParams={changeAutoCompleteHandler}
                    />
                </div>
                <div className="col-12 mt-4">
                    <StepperButtons
                        currentStep={stepIdNumber - 1}
                        handleNextStep={handleNextStep}
                        isBackHidden={true}
                        handleBackStep={handleBackStep}
                    />
                </div>
            </div>
        </Form>
    );
};
